/*
 * Zeitleiste: Balken aus Start und Frist (Kalendertage, inklusive) und Abhängigkeiten.
 * Gerechnet wird mit „JJJJ-MM-TT“ in UTC – Sommerzeit spielt so keine Rolle.
 */
import { calendarDate } from './dates.js';
import type { CardSummary } from './types.js';

export interface Bar {
  start: string;
  end: string;
}

const toUtc = (d: string) => {
  const [y, m, day] = d.split('-').map(Number);
  return Date.UTC(y!, m! - 1, day!);
};
const fromUtc = (t: number) => new Date(t).toISOString().slice(0, 10);

export function addDays(date: string, n: number): string {
  return fromUtc(toUtc(date) + n * 86_400_000);
}

/** b − a in Tagen */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUtc(b) - toUtc(a)) / 86_400_000);
}

/** Start ist immer ein Kalendertag; Frist ggf. mit Uhrzeit (lokaler Tag des Betrachters) */
export function cardBar(c: Pick<CardSummary, 'startAt' | 'dueAt' | 'dueHasTime'> & { startHasTime?: boolean }): Bar | null {
  const start = c.startAt ? calendarDate(c.startAt, !!c.startHasTime) : null;
  const end = c.dueAt ? calendarDate(c.dueAt, c.dueHasTime) : null;
  if (!start && !end) return null;
  const s = start ?? end!;
  const e = end ?? start!;
  return s <= e ? { start: s, end: e } : { start: e, end: s };
}

/** Offene Karten, auf die eine Karte wartet (archivierte/unbekannte zählen nicht) */
export function openBlockers(card: Pick<CardSummary, 'dependsOn'>, byId: Map<string, CardSummary>): CardSummary[] {
  return card.dependsOn.map((id) => byId.get(id)).filter((c): c is CardSummary => !!c && !c.completedAt);
}

export interface DependencyConflict {
  cardId: string;
  blockerId: string;
  /** letzter Tag der Vorgängerkarte */
  blockerEnd: string;
  /** erster Tag der wartenden Karte */
  start: string;
}

/** Termin-Konflikte: Karte beginnt, bevor eine offene Vorgängerkarte fertig sein soll */
export function dependencyConflicts(cards: CardSummary[]): DependencyConflict[] {
  const byId = new Map(cards.map((c) => [c.id, c]));
  const out: DependencyConflict[] = [];
  for (const c of cards) {
    if (c.completedAt || !c.dependsOn.length) continue;
    const bar = cardBar(c);
    if (!bar) continue;
    for (const b of openBlockers(c, byId)) {
      const bb = cardBar(b);
      if (bb && bar.start <= bb.end) out.push({ cardId: c.id, blockerId: b.id, blockerEnd: bb.end, start: bar.start });
    }
  }
  return out;
}

/** Karten, die auf eine bestimmte Karte warten */
export function waitingFor(cardId: string, cards: CardSummary[]): CardSummary[] {
  return cards.filter((c) => c.dependsOn.includes(cardId));
}
